/**
 * @fileoverview
 * Defines routing configuration for the retail section of the application,
 * using lazy-loaded components and access control logic based on subscriptions and roles.
 * Each route supports nested structure and accessConfig for granular control.
 */
import React from "react";
import featureFlag from "../../featureToggler/featureFlag";
import { urlToPageMap } from "../../shared/Utils";
import { Permissions } from "./Permissions";
import { EcmRole } from "./Subscription";
import type { RouteConfig } from "./types";
import { addFullPathToConfig } from "./utils";

/**
 * Lazy-loaded components to improve performance via code-splitting.
 */
const RetailView = React.lazy(() => import("../retail/Retail"));
const RetailCertificate = React.lazy(() => import("../retail/certificate/Certificate"));
const RetailCustomer = React.lazy(() => import("../retail/customer/Customer"));
const CustomerDetails = React.lazy(() => import("../retail/customer/CustomerDetails"));
const CustomerForm = React.lazy(() => import("../retail/customer/CustomerForm"));
const SearchComponent = React.lazy(() => import("../retail/search/SearchComponent"));
const SearchGrid = React.lazy(() => import("../retail/search/SearchGrid"));

/**
 * The route configuration specific to retail functionality. Each route includes
 * path definitions, the component to render (`element`), and accessConfig with
 * allowed roles/subscriptions. Supports nested routing.
 */
const retailRoutesConfig: RouteConfig = Object.freeze(
    addFullPathToConfig({
        retail: {
            path: "/retail",
            element: <RetailView />,
            accessConfig: {
                allowedSubscriptions: featureFlag.retail.index.subscription,
                allowedRETRoles: featureFlag.retail.index.ecmRole || [],
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.retail.index.read],
            },
            children: {
                index: {
                    index: true,
                    element: <SearchComponent />,
                    accessConfig: {
                        allowedSubscriptions: featureFlag.retail.index.subscription,
                        allowedRETRoles: featureFlag.retail.index.ecmRole || [],
                        allowedCEMRoles: [
                            EcmRole.CEMAdmin,
                            EcmRole.CEMManager,
                            EcmRole.CEMPreValidator,
                        ],
                    },
                },
                customerDetails: {
                    path: urlToPageMap.retailCustomerAndCertificates,
                    exact: true,
                    element: <CustomerDetails />,
                    accessConfig: {
                        allowedSubscriptions:
                            featureFlag.retail.editCustomerAndCertificate.subscription,
                        allowedRETRoles: [
                            ...(featureFlag.retail.viewCustomerAndCertificate.ecmRole || []),
                            ...(featureFlag.retail.editCustomerAndCertificate.ecmRole || []),
                        ],
                        allowedCEMRoles: [
                            EcmRole.CEMAdmin,
                            EcmRole.CEMManager,
                            EcmRole.CEMPreValidator,
                        ],
                    },
                },
                customer: {
                    path: urlToPageMap.retailCustomer,
                    element: <RetailCustomer />,
                    accessConfig: {
                        allowedSubscriptions: featureFlag.retail.editCustomer.subscription,
                        allowedRETRoles: [
                            ...(featureFlag.retail.editCustomer.ecmRole || []),
                            ...(featureFlag.retail.viewCustomer.ecmRole || []),
                        ],
                        allowedCEMRoles: [
                            EcmRole.CEMAdmin,
                            EcmRole.CEMManager,
                            EcmRole.CEMPreValidator,
                        ],
                    },
                    children: {
                        index: {
                            index: true,
                            exact: true,
                            element: <CustomerForm />,
                            accessConfig: {
                                allowedSubscriptions: featureFlag.retail.editCustomer.subscription,
                                allowedRETRoles: [
                                    ...(featureFlag.retail.editCustomer.ecmRole || []),
                                    ...(featureFlag.retail.viewCustomer.ecmRole || []),
                                ],
                                allowedCEMRoles: [
                                    EcmRole.CEMAdmin,
                                    EcmRole.CEMManager,
                                    EcmRole.CEMPreValidator,
                                ],
                            },
                        },
                        certificate: {
                            path: urlToPageMap.retailAddCertificate,
                            exact: true,
                            element: <RetailCertificate />,
                            accessConfig: {
                                allowedSubscriptions: featureFlag.retail.editCustomer.subscription,
                                allowedRETRoles: [
                                    ...(featureFlag.retail.editCustomer.ecmRole || []),
                                    ...(featureFlag.retail.viewCustomer.ecmRole || []),
                                ],
                                allowedCEMRoles: [
                                    EcmRole.CEMAdmin,
                                    EcmRole.CEMManager,
                                    EcmRole.CEMPreValidator,
                                ],
                            },
                        },
                    },
                },
                search: {
                    path: urlToPageMap.retailSearch,
                    element: <SearchGrid />,
                    accessConfig: {
                        allowedSubscriptions: featureFlag.retail.search.subscription,
                        allowedRETRoles: featureFlag.retail.search.ecmRole || [],
                        allowedCEMRoles: [
                            EcmRole.CEMAdmin,
                            EcmRole.CEMManager,
                            EcmRole.CEMPreValidator,
                        ],
                    },
                },
            },
        },
    })
);

/**
 * Freezes the retailRoutesConfig object to prevent accidental modifications at runtime.
 * Then builds and exports the full path-based route structure using a helper utility.
 */
export default retailRoutesConfig;
